import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { TokenManager } from '../Services/TokenManager';
import CustomerService from '../Services/CustomerService';

interface LoginType {
  showLoginBasic: boolean; 
  setShowLoginBasic: (value: boolean) => void;
  showLoginStoreCounter: boolean; 
  setShowLoginStoreCounter: (value: boolean) => void;
  closeableLogin?: boolean;
  setCloseableLogin?: (value: boolean) => void;
  showForgotPassword: boolean;
  setShowForgotPassword: (value: boolean) => void;

  token: string | null;
  setToken: CallableFunction;
  guestToken: string | null;
  setGuestToken: CallableFunction;
  tokenErrorRemoveLogin: string;
  setTokenErrorRemoveLogin: CallableFunction;
  
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  
  isAuthenticatedToQuickPurchase: boolean;
  setIsAuthenticatedToQuickPurchase: (value: boolean) => void;

  isAuthenticatedBySSO: boolean;
  setIsAuthenticatedBySSO: (value: boolean) => void;
  hasQuickPurchase: boolean;
  setHasQuickPurchase: (value: boolean) => void;
  
  showButtonQuickPurchase: boolean;
  setShowButtonQuickPurchase: (value: boolean) => void;

  isQuickPurchase: boolean;
  setIsQuickPurchase: (value: boolean) => void;
  
  geolocation: {latitude: number, longitude: number};
  setGeolocation: CallableFunction;
  geolocationIsBlocked: boolean;
  setGeolocationIsBlocked: CallableFunction;
  
  user: any;
  setUser: CallableFunction;

  generateToken: any;
  setGenerateToken: CallableFunction;

  expiratedCode: any;
  setExpiratedCode: CallableFunction;
  
  showLoginSuccessful: any;
  setShowLoginSuccessful: CallableFunction;

  refreshUser: () => Promise<void>;

}

const LoginContext = createContext<LoginType | undefined>(undefined);

export const LoginProvider = ({ children }: { children: ReactNode }) => {

  const [token, setToken] = useState<string | null>(TokenManager.getToken());
  const [guestToken, setGuestToken] = useState<string | null>(TokenManager.getGuestToken());
  const [tokenErrorRemoveLogin, setTokenErrorRemoveLogin] = useState<string>(TokenManager.getTokenErrorRemoveLogin() || '');

  useEffect(() => {
    // reagindo a mudanças vindas do TokenManager
    const unsubscribe = TokenManager.subscribe((newToken) => {
      setToken(TokenManager.getToken());
      setGuestToken(TokenManager.getGuestToken());
      setTokenErrorRemoveLogin(TokenManager.getTokenErrorRemoveLogin() || '');
    });
    return () => unsubscribe();
  }, []);

  const [generateToken, setGenerateToken] = useState(() => {
    const storedGenerateToken = localStorage.getItem('generated_token')
    return storedGenerateToken ? JSON.parse(storedGenerateToken) : null
  })

  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user')
    return storedUser ? JSON.parse(storedUser) : null
  })

  const [showLoginStoreCounter, setShowLoginStoreCounter] = useState(false);
  const [showLoginBasic, setShowLoginBasic] = useState(false);
  const [closeableLogin, setCloseableLogin] = useState(true);
  const [expiratedCode, setExpiratedCode] = useState(false);
  const [showLoginSuccessful, setShowLoginSuccessful] = useState(false);
  const [isQuickPurchase, setIsQuickPurchase] = useState<boolean>(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticatedToQuickPurchase, setIsAuthenticatedToQuickPurchase] = useState(false);
  const [isAuthenticatedBySSO, setIsAuthenticatedBySSO] = useState(false);
  const [hasQuickPurchase, setHasQuickPurchase] = useState(false);
  const [showButtonQuickPurchase, setShowButtonQuickPurchase] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [geolocationIsBlocked, setGeolocationIsBlocked] = useState(false);
  const [geolocation, setGeolocation] = useState(() => {
    const storedGeolocation = localStorage.getItem('position')
    return storedGeolocation ? JSON.parse(storedGeolocation) : null
  })

  useEffect(() => {
    if (geolocation) {
      localStorage.setItem('geolocation', JSON.stringify(geolocation));
      setGeolocationIsBlocked(false);
    } else {
      localStorage.removeItem('geolocation');
    }
  }, [geolocation]);

  useEffect(() => {
    if (showLoginStoreCounter) {
      setShowLoginStoreCounter(true)
      setCloseableLogin!(false)
    }
  }, [showLoginStoreCounter]);

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      TokenManager.setToken(token);
      TokenManager.setGuestToken(null);
    } else {
      localStorage.removeItem('token');
      TokenManager.setToken(null);
      setUser(null);
      setGenerateToken(null);
    }
  }, [token]);

  useEffect(() => {
    if (guestToken) {
      localStorage.setItem('guest_token', guestToken);
      TokenManager.setGuestToken(guestToken);
      TokenManager.setToken(null);
      setUser(null);
    } else {
      localStorage.removeItem('guest_token');
      TokenManager.setGuestToken(null);
    }
  }, [guestToken]);

  useEffect(() => {
    setIsAuthenticated(Boolean(token && user && user.logged));
    setIsAuthenticatedToQuickPurchase(Boolean(token && user && !user?.logged))
  }, [token, user]);

  useEffect(() => {
    if (generateToken) {
      localStorage.setItem('generated_token', JSON.stringify(generateToken));
    } else {
      localStorage.removeItem('generated_token');
    }
  }, [generateToken]);

  const refreshUser = useCallback(async () => {
    if (TokenManager.getToken()) {
      try {
        const customerService = new CustomerService('');
        const profile = await customerService.getProfile();
        if (profile?.profile) {
          setUser(profile.profile);
        }
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TokenManager.getToken()]);

  return (
    <LoginContext.Provider value={{ 
        geolocation,
        setGeolocation,
        geolocationIsBlocked,
        setGeolocationIsBlocked,
        showLoginBasic,
        setShowLoginBasic,
        showLoginStoreCounter,
        setShowLoginStoreCounter,
        closeableLogin,
        setCloseableLogin,
        token,
        setToken,
        user,
        setUser,
        generateToken,
        setGenerateToken,
        expiratedCode,
        setExpiratedCode,
        showLoginSuccessful,
        setShowLoginSuccessful,
        isQuickPurchase,
        setIsQuickPurchase,
        guestToken,
        setGuestToken,
        tokenErrorRemoveLogin, 
        setTokenErrorRemoveLogin,
        isAuthenticated,
        setIsAuthenticated,
        isAuthenticatedToQuickPurchase,
        setIsAuthenticatedToQuickPurchase,
        refreshUser,
        isAuthenticatedBySSO,
        setIsAuthenticatedBySSO,
        hasQuickPurchase,
        setHasQuickPurchase,
        showButtonQuickPurchase,
        setShowButtonQuickPurchase,
        showForgotPassword,
        setShowForgotPassword
      }}>
      {children}
    </LoginContext.Provider>
  );
};

export const useLoginContext = () => {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
